/*
 * ALTO custom scrollbar — touch fallback.
 *
 * iOS Safari (and mobile WebKit/Blink generally) never paints the styled
 * `::-webkit-scrollbar` from tailwind.css: scrollbars there are transient OS
 * overlay indicators that can't be styled or made persistent with CSS. 
 * https://developer.apple.com/forums/thread/670065
 * This script draws a non-interactive overlay thumb over every `.alto-scrollbar`
 * container so the bar is visible on touch devices, matching the desktop look
 * (track #e5e5e5 / thumb #525252). Desktop keeps the CSS scrollbar untouched.
 *
 * The overlay is purely visual (`pointer-events: none`) so it never interferes
 * with native touch scrolling. Axis is auto-detected per element and both axes
 * are supported. Opt out per element with `data-no-custom-scrollbar`; override
 * thickness in px with `data-scrollbar-size`.
 */

(function () {
  // Only enhance where the native styled scrollbar is hidden (touch devices).
  // Desktop with a fine pointer keeps the CSS ::-webkit-scrollbar styling.
  if (!window.matchMedia || !window.matchMedia('(pointer: coarse)').matches) return;

  const THUMB_COLOR = '#525252';
  const TRACK_COLOR = '#e5e5e5';
  const DEFAULT_SIZE = 2; // px, matches desktop ::-webkit-scrollbar
  const MIN_THUMB = 24; // px, keep the thumb tappable/visible on long scrollers

  const enhanced = new WeakSet();

  function makeBar(size) {
    const track = document.createElement('div');
    track.className = 'alto-scrollbar__track';
    track.setAttribute('aria-hidden', 'true');
    track.style.cssText =
      'position:absolute;pointer-events:none;z-index:1;background:' + TRACK_COLOR + ';display:none;';

    const thumb = document.createElement('div');
    thumb.className = 'alto-scrollbar__thumb';
    thumb.style.cssText =
      'display:block;position:absolute;inset:0;background:' + THUMB_COLOR + ';border-radius:' + size + 'px;';

    track.appendChild(thumb);
    return { track, thumb };
  }

  class AltoScrollbar {
    constructor(el) {
      this.el = el;
      this.size = parseInt(el.getAttribute('data-scrollbar-size'), 10) || DEFAULT_SIZE;
      this.x = null; // { track, thumb } for horizontal bar
      this.y = null; // { track, thumb } for vertical bar
      this._frame = 0;

      // The overlay thumbs are absolutely positioned against the element's
      // parent, so the parent must be the offsetParent / a containing block.
      this.parent = el.parentNode;
      if (this.parent && getComputedStyle(this.parent).position === 'static') {
        this.parent.style.position = 'relative';
      }

      this.update = this.update.bind(this);
      this.schedule = this.schedule.bind(this);

      el.addEventListener('scroll', this.schedule, { passive: true });

      if (window.ResizeObserver) {
        this.resizeObserver = new ResizeObserver(this.schedule);
        this.resizeObserver.observe(el);
        if (el.firstElementChild) this.resizeObserver.observe(el.firstElementChild);
      }

      // Content added/removed (e.g. cart items, AJAX-rendered filters) changes
      // scrollWidth/Height without resizing the element itself.
      this.mutationObserver = new MutationObserver(this.schedule);
      this.mutationObserver.observe(el, { childList: true, subtree: true });

      this.update();
    }

    schedule() {
      if (this._frame) return;
      this._frame = requestAnimationFrame(this.update);
    }

    ensureBar(axis) {
      if (this[axis]) return this[axis];
      const bar = makeBar(this.size);
      this.el.parentNode.insertBefore(bar.track, this.el.nextSibling);
      this[axis] = bar;
      return bar;
    }

    update() {
      this._frame = 0;
      const el = this.el;
      if (!el.isConnected) return this.destroy();

      const size = this.size;
      const scrollableX = el.scrollWidth - el.clientWidth > 1;
      const scrollableY = el.scrollHeight - el.clientHeight > 1;

      // Horizontal bar along the bottom edge.
      if (scrollableX) {
        const bx = this.ensureBar('x');
        const viewW = el.clientWidth;
        const thumbW = Math.max(viewW * (viewW / el.scrollWidth), MIN_THUMB);
        const maxScrollX = el.scrollWidth - viewW;
        const thumbX = maxScrollX > 0 ? (el.scrollLeft / maxScrollX) * (viewW - thumbW) : 0;
        bx.track.style.display = 'block';
        bx.track.style.left = el.offsetLeft + 'px';
        bx.track.style.top = el.offsetTop + el.offsetHeight - size + 'px';
        bx.track.style.width = viewW + 'px';
        bx.track.style.height = size + 'px';
        bx.thumb.style.left = thumbX + 'px';
        bx.thumb.style.width = thumbW + 'px';
        bx.thumb.style.top = '0';
        bx.thumb.style.height = size + 'px';
      } else if (this.x) {
        this.x.track.style.display = 'none';
      }

      // Vertical bar along the right edge.
      if (scrollableY) {
        const by = this.ensureBar('y');
        const viewH = el.clientHeight;
        const thumbH = Math.max(viewH * (viewH / el.scrollHeight), MIN_THUMB);
        const maxScrollY = el.scrollHeight - viewH;
        const thumbY = maxScrollY > 0 ? (el.scrollTop / maxScrollY) * (viewH - thumbH) : 0;
        by.track.style.display = 'block';
        by.track.style.left = el.offsetLeft + el.offsetWidth - size + 'px';
        by.track.style.top = el.offsetTop + 'px';
        by.track.style.width = size + 'px';
        by.track.style.height = viewH + 'px';
        by.thumb.style.top = thumbY + 'px';
        by.thumb.style.height = thumbH + 'px';
        by.thumb.style.left = '0';
        by.thumb.style.width = size + 'px';
      } else if (this.y) {
        this.y.track.style.display = 'none';
      }
    }

    destroy() {
      enhanced.delete(this.el);
      this.el.removeEventListener('scroll', this.schedule);
      if (this.resizeObserver) this.resizeObserver.disconnect();
      if (this.mutationObserver) this.mutationObserver.disconnect();
      if (this.x) this.x.track.remove();
      if (this.y) this.y.track.remove();
    }
  }

  function enhance(el) {
    if (enhanced.has(el) || el.hasAttribute('data-no-custom-scrollbar')) return;
    enhanced.add(el);
    // eslint-disable-next-line no-new
    new AltoScrollbar(el);
  }

  function scan(root) {
    (root || document).querySelectorAll('.alto-scrollbar').forEach(enhance);
  }

  function init() {
    // Neutralise any native bar on touch (some Blink browsers still paint the
    // styled ::-webkit-scrollbar) so the overlay thumb is the only one shown.
    const style = document.createElement('style');
    style.textContent =
      '.alto-scrollbar{scrollbar-width:none;}' +
      '.alto-scrollbar::-webkit-scrollbar{display:none;width:0;height:0;}';
    document.head.appendChild(style);

    scan(document);

    // Catch scrollers that AJAX flows *replace* after load — the cart drawer
    // re-render and the collection filters drawer both swap whole elements that
    // contain an `.alto-scrollbar`. We watch the document, but only re-scan when
    // a mutation actually inserts a scroller; unrelated DOM changes (editorial
    // content, section text, etc.) bail on a cheap check and do no work.
    let pending = false;
    const observer = new MutationObserver(function (records) {
      if (pending) return;
      const insertedScroller = records.some(function (record) {
        return Array.prototype.some.call(record.addedNodes, function (node) {
          return (
            node.nodeType === 1 &&
            (node.matches('.alto-scrollbar') || node.querySelector('.alto-scrollbar'))
          );
        });
      });
      if (!insertedScroller) return;
      pending = true;
      requestAnimationFrame(function () {
        pending = false;
        scan(document);
      });
    });
    observer.observe(document.body, { childList: true, subtree: true });

    // Shopify theme editor re-renders sections without a full reload.
    document.addEventListener('shopify:section:load', function (e) {
      scan(e.target);
    });
  }

  // Expose a manual refresh hook for scripts that swap content in place.
  window.AltoScrollbar = { refresh: () => scan(document) };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
